import { useCallback, useEffect, useState } from 'react';
import { platformApi, type OrganizationFeatureDto } from '@/api/platform.api';
import { SUBSCRIPTION_VALIDATION_ENABLED } from '@/config/subscriptionPolicy';
import { useAppSelector } from '@/hooks/useAppSelector';
import type { FeatureCode } from './featureCodes';

const CORE_FEATURES: FeatureCode[] = [
  'POS_BILLING',
  'INVENTORY',
  'EMPLOYEE_BASIC',
  'REPORTING',
  'PROCUREMENT',
];

export function useFeatures() {
  const user = useAppSelector((s) => s.auth.user);
  const authFeatures = useAppSelector((s) => s.auth.enabledFeatures);
  const [features, setFeatures] = useState<OrganizationFeatureDto[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user || user.masterAdmin) {
      setFeatures([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const list = await platformApi.listFeatures();
      setFeatures(list);
    } catch {
      setFeatures(
        CORE_FEATURES.map((code) => ({
          code,
          name: code,
          module: 'CORE',
          enabled: true,
          locked: false,
        })),
      );
    } finally {
      setLoading(false);
    }
  }, [user?.userId, user?.masterAdmin]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enabledSet = new Set(
    features.filter((f) => f.enabled).map((f) => f.code),
  );

  const hasFeature = useCallback(
    (code: FeatureCode | string) => {
      if (!SUBSCRIPTION_VALIDATION_ENABLED) return true;
      if (user?.masterAdmin) return true;
      if (authFeatures.length > 0) {
        return authFeatures.includes(code);
      }
      if (features.length === 0) {
        return CORE_FEATURES.includes(code as FeatureCode);
      }
      return enabledSet.has(code);
    },
    [user?.masterAdmin, authFeatures, features.length, enabledSet],
  );

  return { features, loading, refresh, hasFeature };
}
